/**
 * The turn log (docs/ux.md §11.1, spec v2 §16): what happened, in short lines with an icon
 * each, grouped by turn; the newest at the bottom and announced politely. Chips filter by kind;
 * an entry about places on the map has a button that points them out on the board.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as styles from './Game.css.ts';
import type { History } from './history.ts';
import { Icon, type IconName } from './icons.tsx';
import { logEntries, type LogEntry, type LogKind } from './log.ts';

const ICONS: Readonly<Record<LogKind, IconName>> = {
  wind: 'wind',
  move: 'sail',
  fire: 'cannon',
  hit: 'burst',
  repair: 'wrench',
  capture: 'fort',
  turn: 'hourglass',
  game: 'flag',
};

type Filter = 'all' | 'wind' | 'move' | 'fire' | 'repair' | 'turn';

const FILTERS: Readonly<Record<Filter, readonly LogKind[] | null>> = {
  all: null,
  wind: ['wind'],
  move: ['move'],
  fire: ['fire', 'hit'],
  repair: ['repair', 'capture'],
  turn: ['turn', 'game'],
};

export function TurnLog({ history, onPick }: { history: History; onPick: (entry: LogEntry) => void }) {
  const { t } = useTranslation();
  const all = useMemo(() => logEntries(t, history), [t, history]);
  const [filter, setFilter] = useState<Filter>('all');
  const kinds = FILTERS[filter];
  const entries = kinds ? all.filter((e) => kinds.includes(e.kind)) : all;
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries.length]);

  const groups: { turn: number; side: LogEntry['side']; entries: LogEntry[] }[] = [];
  for (const e of entries) {
    const last = groups.at(-1);
    if (last?.turn === e.turn) last.entries.push(e);
    else groups.push({ turn: e.turn, side: e.side, entries: [e] });
  }
  const players = history.states[0]?.players;

  return (
    <section className={styles.log} aria-label={t('game.log.title')}>
      <h2 className={styles.fireTitle}>{t('game.log.title')}</h2>
      <div className={styles.rosterChips} role="group" aria-label={t('game.roster.filter.label')}>
        {(Object.keys(FILTERS) as Filter[]).map((f) => {
          const k = FILTERS[f];
          const n = k ? all.filter((e) => k.includes(e.kind)).length : all.length;
          return (
            <button
              key={f}
              type="button"
              className={styles.rosterChip}
              aria-pressed={filter === f}
              onClick={() => {
                setFilter(f);
              }}
            >
              {t(`game.log.filter.${f}`)} <span className={styles.rosterChipCount}>{n}</span>
            </button>
          );
        })}
      </div>
      {entries.length === 0 ? <p className={styles.hint}>{t('game.log.empty')}</p> : null}
      <ol ref={listRef} className={styles.logList} role="log" aria-live="polite">
        {groups.map((g) => (
          <li key={g.turn}>
            <div className={styles.logTurn}>
              {t('game.log.turn', { n: g.turn, side: players?.[g.side].name ?? g.side })}
            </div>
            <ul className={styles.logEntries}>
              {g.entries.map((e) => (
                <li key={e.key} className={styles.logEntry} data-tone={e.tone} data-side={e.side}>
                  <span className={styles.logIcon} data-kind={e.kind} aria-hidden="true">
                    <Icon name={ICONS[e.kind]} size={15} />
                  </span>
                  <span className={styles.logText}>{e.text}</span>
                  {e.cells.length > 0 ? (
                    <button
                      type="button"
                      className={styles.rosterCentre}
                      aria-label={`${t('game.log.show')}: ${e.text}`}
                      data-tip={t('game.log.show')}
                      onClick={() => {
                        onPick(e);
                      }}
                    >
                      <Icon name="centre" size={16} />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </section>
  );
}
